import { supplierApi } from '@/main/api';
import Card from '@/main/components/shared/card/card';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KOGAutoCompletePaginationControlledWrapper from '@/main/components/shared/inputAutoCompleteWithPagination/autoCompletePaginationControlled';
import KogInputText from '@/main/components/shared/inputText/inputText';
import KogInputAmountControlled from '@/main/components/shared/inputText/inputAmountControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import { KeyValueDefault } from '@/main/models/common';
import { SupplierFavoriteRequest } from '@/main/models/request';
import { ReassignForm } from '@/main/screens/order/reassignOrder/reassignScreenUtils';
import { getTranslateByKey } from '@/main/services/utils';
import { useVisualIdentityReducer } from '@/main/store/visualIdentity';
import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import * as S from './styles';

interface IDirectAssignRow {
  partIndex: number;
  guidCompanyBuyer?: string;
  showPrices: boolean;
  partTypeOptions: KeyValueDefault[];
}

const InputWrapper = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{ width: '100%' }}
    onClick={e => e.stopPropagation()}
    onMouseDown={e => e.stopPropagation()}>
    {children}
  </div>
);

const ID_BASE = 'id-reassignOrder';

function DirectAssignRow({
  partIndex,
  guidCompanyBuyer,
  showPrices,
  partTypeOptions
}: Readonly<IDirectAssignRow>) {
  const { control, setValue } = useFormContext<ReassignForm>();
  const { visualIdentity: visualIdentityReducer } = useVisualIdentityReducer();

  const fieldBase = `parts.${partIndex}.directAssign` as const;
  const rowId = `${partIndex}-directAssign`;
  const supplierLabel = getTranslateByKey('information.supplier');

  const supplierValue = useWatch({ control, name: `${fieldBase}.supplier` });
  const [supplierActivated, setSupplierActivated] = useState(false);
  const showRealSupplier = supplierActivated || supplierValue != null;

  const partTypeValue = useWatch({ control, name: `${fieldBase}.partType` });
  const [partTypeActivated, setPartTypeActivated] = useState(false);
  const showRealPartType = partTypeActivated || partTypeValue != null;
  const partTypeLabel = getTranslateByKey('information.partTypes');

  return (
    <Card
      className="box-layout-card-collumn"
      borderColor={visualIdentityReducer.colors.pantone2726C}
      style={{
        position: 'relative',
        width: '100%',
        padding: '0',
        minWidth: '1100px'
      }}>
      <S.NewSupplierBadge>
        {getTranslateByKey('information.newSupplier')}
      </S.NewSupplierBadge>

      <S.DirectAssignRowContainer>
        <S.DirectAssignSupplierCol>
          <InputWrapper>
            {showRealSupplier ? (
              <KOGAutoCompletePaginationControlledWrapper
                name={`${fieldBase}.supplier`}
                label={supplierLabel}
                loadingMessage={`${getTranslateByKey(
                  'information.searchingForSuppliers'
                )}...`}
                idScreen={`${ID_BASE}-supplier`}
                control={control}
                required
                autoFocus={supplierActivated}
                getData={async requestProp => {
                  const request: SupplierFavoriteRequest = {
                    ...requestProp,
                    guidCompanyBuyer: String(guidCompanyBuyer)
                  };
                  return supplierApi.getFavoriteSuppliersByCompanyBuyer(
                    request
                  );
                }}
              />
            ) : (
              <KogInputText
                id={`${ID_BASE}-supplier-placeholder-${rowId}`}
                label={supplierLabel}
                variant="filled"
                required
                inputProps={{ readOnly: true }}
                onFocus={() => setSupplierActivated(true)}
              />
            )}
          </InputWrapper>
        </S.DirectAssignSupplierCol>

        <S.DirectAssignPartTypeCol>
          <InputWrapper>
            {showRealPartType ? (
              <KogAutoCompleteControlled
                id={`${ID_BASE}-select-partType-${rowId}`}
                nameCustom={`${fieldBase}.partType`}
                label={partTypeLabel}
                control={control}
                setValueCustom={setValue}
                options={partTypeOptions}
                isLoading={false}
                onChangeCustom={() => { }}
                required
                rulesCustom={{ required: true }}
                autoFocus={partTypeActivated}
              />
            ) : (
              <KogInputText
                id={`${ID_BASE}-partType-placeholder-${rowId}`}
                label={partTypeLabel}
                variant="filled"
                required
                inputProps={{ readOnly: true }}
                onFocus={() => setPartTypeActivated(true)}
              />
            )}
          </InputWrapper>
        </S.DirectAssignPartTypeCol>

        <S.DirectAssignDeadlineCol>
          <InputWrapper>
            <KogInputTextControlledNew
              id={`${ID_BASE}-input-deliveryDeadline-${rowId}`}
              name={`${fieldBase}.deliveryDeadline`}
              control={control}
              label={getTranslateByKey('information.deliveryDeadline')}
              onChangeCustom={() => { }}
              required
              hasOnlyNumber
              hasOnlyNumberInteger
            />
          </InputWrapper>
        </S.DirectAssignDeadlineCol>

        {showPrices && (
          <S.DirectAssignPriceCol>
            <InputWrapper>
              <KogInputAmountControlled
                typeInput="filled"
                id={`${ID_BASE}-input-price-${rowId}`}
                name={`${fieldBase}.price`}
                label={getTranslateByKey('information.price')}
                control={control}
                onClickCustom={e => e.stopPropagation()}
                required
              />
            </InputWrapper>
          </S.DirectAssignPriceCol>
        )}
      </S.DirectAssignRowContainer>
    </Card>
  );
}

export default React.memo(DirectAssignRow);
